import dayjs from 'dayjs'
import updateLocale from 'dayjs/plugin/updateLocale'
import 'dayjs/locale/vi'

// The languages the app ships, and where the choice lives.
//
//   * Chosen language = localStorage `lang` (read once at startup by
//     src/configs/i18n/index.ts, written by the topbar LanguageSwitch).
//     No backend field: the account profile has no language and none is read.
//   * Default and fallback language = `vi`.
//   * dayjs follows i18n (`syncLocale`, run on every `languageChanged`), and
//     the DS UIProvider locale follows it in components/AppUIProvider.
//     Pages must not call `dayjs.locale('vi')` themselves: it resets the
//     global locale when their lazy chunk loads.

export const SUPPORTED_LANGUAGES = [
    // Each language is named in itself, whatever language is displayed.
    // `short` is the code shown on the topbar button (a code, spelled as is).
    { code: 'vi', short: 'VI', label: 'Tiếng Việt' },
    { code: 'en', short: 'EN', label: 'English' },
] as const

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code']

const DEFAULT_LANGUAGE: LanguageCode = 'vi'
const LANGUAGE_STORAGE_KEY = 'lang'

const isSupportedLanguage = (code: unknown): code is LanguageCode =>
    SUPPORTED_LANGUAGES.some((l) => l.code === code)

/** `en-US` -> `en`; anything unsupported -> the default. */
export const toSupportedLanguage = (code?: string | null): LanguageCode => {
    const base = code?.split('-')[0]
    return isSupportedLanguage(base) ? base : DEFAULT_LANGUAGE
}

export function getStoredLanguage(): LanguageCode {
    try {
        return toSupportedLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY))
    } catch {
        return DEFAULT_LANGUAGE
    }
}

export function persistLanguage(code: LanguageCode): void {
    try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, code)
    } catch {
        // storage blocked: the choice lasts until reload
    }
}

dayjs.extend(updateLocale)
// Weeks start on Monday in both languages, so "this week" covers the same days
// whichever language is displayed.
dayjs.updateLocale('vi', { weekStart: 1 })
dayjs.updateLocale('en', { weekStart: 1 })

/** dayjs locale and <html lang> follow the displayed language. */
export function syncLocale(code?: string | null): void {
    const lang = toSupportedLanguage(code)
    dayjs.locale(lang)
    if (typeof document !== 'undefined') document.documentElement.lang = lang
}
